// src/features/simulators/shared/components/SimulationResults.tsx

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Cancel as FailIcon,
  Assignment as LogIcon,
  Http as ApiIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { EventLog } from './EventLog';
import { ApiRequestLog } from './ApiRequestLog';
import type { SimulationSummary } from '../hooks/useSimulatorWizard';
import type { LogEntry, CapturedRequest } from '../types/simulator.types';

interface SimulationResultsProps {
  summary: SimulationSummary;
  logs: LogEntry[];
  capturedRequests?: CapturedRequest[];
  simulatorName?: string;
  children?: React.ReactNode;
}

interface StatCardProps {
  label: string;
  value: string | number;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        textAlign: 'center',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {label}
      </Typography>
    </Paper>
  );
};

export const SimulationResults: React.FC<SimulationResultsProps> = ({
  summary,
  logs,
  capturedRequests = [],
  simulatorName,
  children,
}) => {
  const translate = useTranslate();
  const theme = useTheme();

  const durationSeconds = (summary.duration / 1000).toFixed(1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Success/Fail Banner */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        py: 3,
        px: 2,
        borderRadius: 1,
        backgroundColor: summary.success
          ? `${theme.palette.success.main}08`
          : `${theme.palette.error.main}08`,
        border: `1px solid ${summary.success
          ? `${theme.palette.success.main}30`
          : `${theme.palette.error.main}30`
        }`,
      }}>
        {summary.success ? (
          <SuccessIcon sx={{ fontSize: 48, color: theme.palette.success.main }} />
        ) : (
          <FailIcon sx={{ fontSize: 48, color: theme.palette.error.main }} />
        )}

        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {summary.success
            ? translate('resources.simulators.wizard.result_success')
            : translate('resources.simulators.wizard.result_failed')
          }
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {translate('resources.simulators.wizard.result_duration', { duration: durationSeconds })}
          {' | '}
          {translate('resources.simulators.wizard.result_events', { count: summary.totalEvents })}
        </Typography>

        {summary.error && (
          <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
            {summary.error}
          </Typography>
        )}
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label={translate('resources.simulators.stats.successful')}
            value={summary.successful}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label={translate('resources.simulators.stats.failed')}
            value={summary.failed}
            color={theme.palette.error.main}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label={translate('resources.simulators.stats.abandoned')}
            value={summary.abandoned}
            color={theme.palette.warning.main}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label={translate('resources.simulators.stats.events')}
            value={summary.totalEvents}
            color={theme.palette.primary.main}
          />
        </Grid>
      </Grid>

      {/* Simulator-specific extra content */}
      {children}

      {/* Logs: two-column on lg+, stacked on smaller */}
      <Grid container spacing={2}>
        {/* API Request Log */}
        {capturedRequests.length > 0 && (
          <Grid item xs={12} lg={8}>
            <MyFormSection
              title={translate('resources.simulators.wizard.api_log_title')}
              icon={<ApiIcon />}
              variant="card"
              collapsible
              defaultExpanded={false}
            >
              <ApiRequestLog requests={capturedRequests} simulatorName={simulatorName} />
            </MyFormSection>
          </Grid>
        )}

        {/* Activity Log */}
        <Grid item xs={12} lg={capturedRequests.length > 0 ? 4 : 12}>
          <MyFormSection
            title={translate('resources.simulators.wizard.activity_log')}
            icon={<LogIcon />}
            variant="card"
            collapsible
            defaultExpanded={false}
          >
            <EventLog logs={logs} maxHeight={400} showFilter={false} showSearch={false} />
          </MyFormSection>
        </Grid>
      </Grid>
    </Box>
  );
};
